import { useMutation } from '@apollo/client';
import { AlertService, Button, DropdownDialog, InformationIconSmall, MadLib, Theme } from '@spoiler-alert/ui-library';
import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { SetOpportunitiesRules } from '../../graphql/mutations';
import { opportunitiesSettingsQuery } from '../../graphql/queries';

const styles = {
  adjustThreshold: {
    fontSize: 14,
    fontWeight: 400,
  },
  thresholdDialog: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    '& input': {
      width: '40px !important',
    },
    '& > p': {
      margin: 0,
    },
  },
  thresholdAnchor: {
    position: 'relative',
    display: 'inline-block',
  },
  thresholdDropdown: {
    left: 'auto',
    right: 0,
    transform: 'none',
    width: 'max-content',
    maxWidth: 'calc(100vw - 48px)',
    '&:before': {
      left: 'auto',
      right: 90,
    },
    '&:after': {
      left: 'auto',
      right: 91,
    },
  },
  maxFieldError: {
    '& div:has(> input[name="field-upperCutoff"])': {
      borderBottom: `solid 2px ${Theme.red}`,
    },
  },
  errorText: {
    marginTop: '4px',
    display: 'flex',
    textAlign: 'flex-start',
    color: Theme.red,
    fontSize: 12,
    fontWeight: 400,
    '& svg': {
      height: 16,
      width: 16,
      fill: Theme.red,
      marginRight: 4,
    },
  },
};

const useStyles = createUseStyles(styles);

interface ThresholdValues {
  lowerCutoff: number | null;
  upperCutoff: number | null;
}

interface ReservePriceThresholdSettingsProps {
  site: {
    opportunitiesSettings?: {
      lowerCutoff?: number | null;
      upperCutoff?: number | null;
    } | null;
  };
}

const ReservePriceThresholdSettings: React.FC<ReservePriceThresholdSettingsProps> = ({ site }) => {
  const classes = useStyles();
  const [setOpportunitiesRules, { loading: loadingSetRules }] = useMutation(SetOpportunitiesRules);
  const [showThreshold, setShowThreshold] = useState(false);
  const [reservePriceValues, setReservePriceValues] = useState<ThresholdValues>({
    lowerCutoff: site?.opportunitiesSettings?.lowerCutoff ?? 50,
    upperCutoff: site?.opportunitiesSettings?.upperCutoff ?? 100,
  });
  const { lowerCutoff: min, upperCutoff: max } = reservePriceValues;
  const minError = min == null;
  const maxError = (max !== null && min !== null && max < min) || max == null;
  const invalidRules = minError || maxError;

  const handleThresholdChange = (value: string | number, field: string) => {
    const key = field as keyof ThresholdValues;
    const parsed = typeof value === 'number' ? value : Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) return setReservePriceValues((prev) => ({ ...prev, [key]: null }));
    if (parsed < 0 || parsed > 100) return;
    setReservePriceValues((prev) => ({ ...prev, [key]: parsed }));
  };

  const dialogClass = () => {
    if (maxError && max !== null) return `${classes.thresholdDialog} ${classes.maxFieldError}`;
    return classes.thresholdDialog;
  };

  const showSaveError = () =>
    AlertService.alert({
      type: 'warning',
      message: (
        <span>
          We were unable to save the rules due to an unknown error. Our team has been notified and are looking into the issue. Please contact customer
          support if the issue persists.
        </span>
      ),
      autoDismiss: true,
      dismissDelay: 5000,
    });

  const saveRules = async () => {
    let result;
    try {
      result = await setOpportunitiesRules({
        variables: {
          lowerCutoff: reservePriceValues.lowerCutoff,
          upperCutoff: reservePriceValues.upperCutoff,
        },
        refetchQueries: ['trucklaneExceptionsQuery', { query: opportunitiesSettingsQuery }],
      });
    } catch (error) {
      return showSaveError();
    }
    if (result.data?.setOpportunitiesRules?.errors?.length > 0) {
      return showSaveError();
    }
    return AlertService.alert({
      type: 'success',
      message: <span>Successfully saved opportunities rules</span>,
      autoDismiss: true,
      dismissDelay: 5000,
    });
  };

  return (
    <div className={classes.thresholdAnchor}>
      <Button link className={classes.adjustThreshold} onClick={() => setShowThreshold(!showThreshold)}>
        Adjust reserve price threshold
      </Button>
      {showThreshold && (
        <DropdownDialog className={classes.thresholdDropdown} onOutsideClick={() => setShowThreshold(false)}>
          <div className={dialogClass()}>
            <p>SHOW OPPORTUNITIES THAT ARE</p>
            <MadLib
              template={'between [lowerCutoff:integer]% and [upperCutoff:integer]% of reserve price.'}
              values={{ lowerCutoff: min ?? '', upperCutoff: max ?? '' }}
              onChange={handleThresholdChange}
            />

            {minError && (
              <div className={classes.errorText}>
                <InformationIconSmall /> Lower cutoff percent must be set
              </div>
            )}

            {maxError && (
              <div className={classes.errorText}>
                <InformationIconSmall />
                {min !== null ? `Upper cutoff percent must be higher than ${min}%` : 'Upper cutoff percent must be set'}
              </div>
            )}

            <Button disabled={invalidRules} onClick={saveRules} loadingText="Saving Rules" loading={loadingSetRules}>
              Save Rules
            </Button>
          </div>
        </DropdownDialog>
      )}
    </div>
  );
};

export default ReservePriceThresholdSettings;
